import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import {
  bulkCreateLscMemberInvites,
  createLscMember,
  createLscMemberInvite,
  listLscMemberClaims,
  listLscMemberInvites,
  listLscMembers,
  getLscMemberTeamOptions,
  reinviteLscMember,
  removeLscMember,
  restoreLscMember,
  unclaimLscMember,
  updateLscMember,
  LscApiError,
} from '../api';
import type { LscMember, LscMemberCounts, LscMemberInviteSummary, LscMemberTeamOption } from '../types';
import MembersTable from '../components/MembersTable';
import {
  formatBulkInviteClipboard,
  formatMemberInviteMessage,
  leagueHasNeonSportzImport,
  teamConnectionHeadline,
} from '../memberConnection';
import { copyTextToClipboard } from '../copyToClipboard';
import { memberClaimSignInMethod } from '../utils/memberStatus';
import TeamAssignmentConflictBanner from '../components/TeamAssignmentConflictBanner';
import MemberStatusFilterBar from '../components/MemberStatusFilterBar';
import ImportedMembersPromotionPanel from '../components/ImportedMembersPromotionPanel';
import NeonSportzMemberSyncPanel from '../components/NeonSportzMemberSyncPanel';
import MemberClaimSuggestionPanel from '../components/MemberClaimSuggestionPanel';
import MemberClaimQueuePanel from '../components/MemberClaimQueuePanel';
import MemberFormModal from '../components/MemberFormModal';
import RemoveMemberConfirmModal from '../components/RemoveMemberConfirmModal';
import RestoreMemberModal from '../components/RestoreMemberModal';
import LeagueInvitePanel from '../components/LeagueInvitePanel';
import { useLscContentColumn } from '../lscContentColumn';
import JoinRequestQueuePanel from '../components/JoinRequestQueuePanel';
import LoadingState from '../components/LoadingState';
import LscMutationErrorAlert from '../components/LscMutationErrorAlert';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import { lscReturnTo } from '../routing';
import {
  DEFAULT_MEMBER_STATUS_FILTER,
  emptyFilterMessage,
  filterMembersByStatus,
  type MemberStatusFilter,
} from '../utils/memberFilter';
import {
  buildMemberTeamAssignmentPatch,
  validateCustomTeamName,
  type MemberFormValues,
} from '../utils/memberTeamAssignment';
import { isLeagueArchived } from '../lifecycle';

const TEAM_ALREADY_CLAIMED_MESSAGE = 'That team is already assigned to another member.';

export function buildMemberUpdatePayload(
  values: MemberFormValues,
  mode: 'create' | 'edit'
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    display_name: values.display_name.trim(),
    role: values.role,
    status: values.status,
  };
  const uid = values.user_uid.trim();
  payload.user_uid = uid || null;
  const login = values.twitch_login.trim();
  payload.twitch_login = login || null;
  const twitchId = values.twitch_user_id.trim();
  payload.twitch_user_id = twitchId || null;
  if (mode === 'edit') {
    payload.stream_platform = values.stream_platform;
    if (values.stream_platform === 'youtube') {
      payload.youtube_channel = values.youtube_channel.trim();
    }
  }

  const teamPatch = buildMemberTeamAssignmentPatch(values, mode);
  if (teamPatch) {
    Object.assign(payload, teamPatch);
  }
  return payload;
}

const LscMembersPage: React.FC = () => {
  useLscContentColumn('narrow');
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/members`)}>
      <MembersContent />
    </LscAuthGate>
  );
};

function MembersContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { league, isCommissioner } = useLeague();
  const [members, setMembers] = useState<LscMember[]>([]);
  const [memberCounts, setMemberCounts] = useState<LscMemberCounts | null>(null);
  const [pendingClaimMemberIds, setPendingClaimMemberIds] = useState<Set<string>>(new Set());
  const [memberInvites, setMemberInvites] = useState<Record<string, LscMemberInviteSummary>>({});
  const [memberClaims, setMemberClaims] = useState<Record<string, LscMemberInviteSummary>>({});
  const [invitingMemberId, setInvitingMemberId] = useState<string | null>(null);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkList, setBulkList] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingMember, setEditingMember] = useState<LscMember | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<MemberStatusFilter>(DEFAULT_MEMBER_STATUS_FILTER);
  const [removeTarget, setRemoveTarget] = useState<LscMember | null>(null);
  const [removing, setRemoving] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<LscMember | null>(null);
  const [restoreTeams, setRestoreTeams] = useState<LscMemberTeamOption[]>([]);
  const [restoreTeamsLoading, setRestoreTeamsLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!leagueId) return;
    setError(null);
    try {
      const data = await listLscMembers(leagueId);
      setMembers(data.members);
      setMemberCounts(data.counts);
      if (isCommissioner) {
        try {
          const invites = await listLscMemberInvites(leagueId);
          const byMember: Record<string, LscMemberInviteSummary> = {};
          const claims: Record<string, LscMemberInviteSummary> = {};
          for (const invite of invites) {
            const prev = byMember[invite.member_id];
            if (!prev || (invite.created_at || '') > (prev.created_at || '')) {
              byMember[invite.member_id] = invite;
            }
            if (invite.status === 'claimed') {
              const prior = claims[invite.member_id];
              if (!prior || (invite.claimed_at || '') > (prior.claimed_at || '')) {
                claims[invite.member_id] = invite;
              }
            }
          }
          setMemberInvites(byMember);
          setMemberClaims(claims);
        } catch {
          setMemberInvites({});
          setMemberClaims({});
        }
        try {
          const claimsData = await listLscMemberClaims(leagueId);
          const pendingIds = new Set(
            (claimsData.claims || [])
              .filter((claim) => claim.status === 'pending' && claim.member_id)
              .map((claim) => claim.member_id as string)
          );
          setPendingClaimMemberIds(pendingIds);
        } catch {
          setPendingClaimMemberIds(new Set());
        }
      }
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not load members.'));
    } finally {
      setLoading(false);
    }
  }, [leagueId, isCommissioner]);

  const rememberInviteUrl = useCallback((memberId: string, claimUrl: string) => {
    setMemberInvites((current) => {
      const prev = current[memberId];
      return {
        ...current,
        [memberId]: {
          id: prev?.id || memberId,
          league_id: prev?.league_id || leagueId || '',
          member_id: memberId,
          status: 'active',
          valid: true,
          expires_at: prev?.expires_at,
          created_at: prev?.created_at,
          claim_url: claimUrl,
        },
      };
    });
  }, [leagueId]);

  const ensureInviteUrl = useCallback(
    async (member: LscMember): Promise<string | null> => {
      if (!leagueId) return null;
      const existing = memberInvites[member.id];
      if (existing?.valid && existing.claim_url) return existing.claim_url;
      setInvitingMemberId(member.id);
      try {
        const issued = existing?.valid
          ? await reinviteLscMember(leagueId, member.id)
          : await createLscMemberInvite(leagueId, member.id);
        rememberInviteUrl(member.id, issued.claim_url);
        return issued.claim_url;
      } finally {
        setInvitingMemberId(null);
      }
    },
    [leagueId, memberInvites, rememberInviteUrl]
  );

  const copyMemberLink = useCallback(
    async (member: LscMember) => {
      setError(null);
      try {
        const url = await ensureInviteUrl(member);
        if (!url) return;
        const copied = await copyTextToClipboard(url);
        setToast(copied ? `Link copied for ${member.display_name}` : 'Could not copy the link.');
        await loadMembers();
      } catch (err) {
        setError(parseLscMutationError(err, 'Could not create an invite link.'));
      }
    },
    [ensureInviteUrl, loadMembers]
  );

  const copyMemberMessage = useCallback(
    async (member: LscMember) => {
      setError(null);
      try {
        const url = await ensureInviteUrl(member);
        if (!url) return;
        const message = formatMemberInviteMessage({
          displayName: member.display_name,
          teamLabel: member.current_side_label,
          leagueName: league?.name,
          claimUrl: url,
          signInMethod: memberClaimSignInMethod(member),
        });
        const copied = await copyTextToClipboard(message);
        setToast(copied ? `Message copied for ${member.display_name}` : 'Could not copy the message.');
        await loadMembers();
      } catch (err) {
        setError(parseLscMutationError(err, 'Could not create an invite link.'));
      }
    },
    [ensureInviteUrl, league?.name, loadMembers]
  );

  const issueInvite = useCallback(
    async (member: LscMember, reinvite: boolean) => {
      if (!leagueId) return;
      setInvitingMemberId(member.id);
      setError(null);
      try {
        const issued = reinvite
          ? await reinviteLscMember(leagueId, member.id)
          : await createLscMemberInvite(leagueId, member.id);
        rememberInviteUrl(member.id, issued.claim_url);
        const copied = await copyTextToClipboard(issued.claim_url);
        setToast(
          copied
            ? `New link copied for ${member.display_name}`
            : `New link ready for ${member.display_name}`
        );
        await loadMembers();
      } catch (err) {
        setError(parseLscMutationError(err, 'Could not create an invite link.'));
      } finally {
        setInvitingMemberId(null);
      }
    },
    [leagueId, loadMembers, rememberInviteUrl]
  );

  const issueBulkInvites = useCallback(async () => {
    if (!leagueId) return;
    setBulkWorking(true);
    setError(null);
    try {
      const issued = await bulkCreateLscMemberInvites(leagueId);
      const text = formatBulkInviteClipboard(issued.invites || []);
      setBulkList(text);
      if (!text) {
        setToast('Every member is already claimed.');
      } else {
        const copied = await copyTextToClipboard(text);
        setToast(copied ? 'Unclaimed links copied' : 'Links are ready below. Copy them from the box.');
      }
      await loadMembers();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not create invite links.'));
    } finally {
      setBulkWorking(false);
    }
  }, [leagueId, loadMembers]);

  const unclaimMember = useCallback(
    async (member: LscMember) => {
      if (!leagueId) return;
      const confirmed = window.confirm(
        `Unclaim ${member.display_name}? Their Twitch account will be unlinked and live detection for that account will stop. You can invite the right person after.`
      );
      if (!confirmed) return;
      setInvitingMemberId(member.id);
      setError(null);
      try {
        await unclaimLscMember(leagueId, member.id);
        await loadMembers();
      } catch (err) {
        setError(parseLscMutationError(err, 'Could not unclaim this member.'));
      } finally {
        setInvitingMemberId(null);
      }
    },
    [leagueId, loadMembers]
  );

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const openCreate = () => {
    setModalMode('create');
    setEditingMember(null);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (member: LscMember) => {
    setModalMode('edit');
    setEditingMember(member);
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (values: MemberFormValues) => {
    if (!leagueId) return;
    if (values.team_assignment_mode === 'custom' && !values.resolve_team_conflict) {
      const validationError = validateCustomTeamName(values.custom_team_name);
      if (validationError) {
        setFormError(validationError);
        return;
      }
    }
    setSaving(true);
    setError(null);
    setFormError(null);
    try {
      const payload = buildMemberUpdatePayload(values, modalMode);
      if (modalMode === 'create') {
        await createLscMember(leagueId, payload);
      } else if (editingMember) {
        await updateLscMember(leagueId, editingMember.id, payload);
      }
      setModalOpen(false);
      await loadMembers();
    } catch (err) {
      if (err instanceof LscApiError && err.code === 'team_already_claimed') {
        setFormError(TEAM_ALREADY_CLAIMED_MESSAGE);
        return;
      }
      const parsed = parseLscMutationError(err, 'Could not save member.');
      setFormError(parsed.message);
      setError(parsed);
    } finally {
      setSaving(false);
    }
  };

  const openRestore = async (member: LscMember) => {
    setRestoreTarget(member);
    setRestoreTeams([]);
    if ((member.current_side_label || '').trim() || !leagueId) return;
    setRestoreTeamsLoading(true);
    try {
      const options = await getLscMemberTeamOptions(leagueId, {
        current_member_id: member.id,
        unclaimed_only: true,
      });
      setRestoreTeams(options.teams || []);
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not load teams.'));
      setRestoreTeams([]);
    } finally {
      setRestoreTeamsLoading(false);
    }
  };

  const handleRestore = async (teamLabel?: string) => {
    if (!leagueId || !restoreTarget) return;
    setRestoring(true);
    setError(null);
    try {
      await restoreLscMember(leagueId, restoreTarget.id, teamLabel);
      setRestoreTarget(null);
      setStatusFilter(DEFAULT_MEMBER_STATUS_FILTER);
      await loadMembers();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not restore member.'));
    } finally {
      setRestoring(false);
    }
  };

  const handleRemove = async () => {
    if (!leagueId || !removeTarget) return;
    setRemoving(true);
    setError(null);
    try {
      await removeLscMember(leagueId, removeTarget.id);
      setRemoveTarget(null);
      await loadMembers();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not remove member.'));
    } finally {
      setRemoving(false);
    }
  };

  if (loading) return <LoadingState />;

  const archived = league ? isLeagueArchived(league.status) : false;
  const filteredMembers = filterMembersByStatus(members, statusFilter);
  const importedLeague = leagueHasNeonSportzImport(league);
  const headline = teamConnectionHeadline(members);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <h1 className="text-2xl font-bold text-white mb-1">Members</h1>
          <p className="text-sm text-secondary-200" data-testid="members-connection-headline">
            {isCommissioner
              ? headline
              : 'Connect Twitch accounts so LSC can detect live league games.'}
          </p>
        </div>
        {isCommissioner && (
          <button
            type="button"
            className="btn-secondary whitespace-nowrap"
            onClick={() => void issueBulkInvites()}
            disabled={archived || bulkWorking}
            data-testid="bulk-invite-members-btn"
          >
            {bulkWorking ? 'Working...' : 'Copy all unclaimed links'}
          </button>
        )}
      </div>
      {toast ? (
        <p className="text-sm text-emerald-300" role="status" data-testid="members-copy-toast">
          {toast}
        </p>
      ) : null}
      {bulkList ? (
        <details className="text-sm text-secondary-300" data-testid="bulk-invite-list">
          <summary className="cursor-pointer text-primary-300">Copied links</summary>
          <textarea
            readOnly
            value={bulkList}
            rows={6}
            className="mt-2 w-full rounded-md border border-secondary-600 bg-secondary-900 px-3 py-2 text-sm text-white"
            data-testid="bulk-invite-list-text"
            aria-label="Invite links"
          />
        </details>
      ) : null}
      {isCommissioner && leagueId && !importedLeague ? (
        <LeagueInvitePanel
          leagueId={leagueId}
          leagueName={league?.name}
          leagueArchived={archived}
          unassignedJoin
        />
      ) : null}
      <LscMutationErrorAlert error={error} />
      {isCommissioner ? (
        <TeamAssignmentConflictBanner
          conflictCount={memberCounts?.team_assignment_conflict_count ?? 0}
          visible={Boolean(isCommissioner)}
        />
      ) : null}
      {!isCommissioner && leagueId && (
        <MemberClaimSuggestionPanel
          leagueId={leagueId}
          onClaimSubmitted={() => void loadMembers()}
        />
      )}
      {isCommissioner ? (
        <MemberStatusFilterBar members={members} value={statusFilter} onChange={setStatusFilter} />
      ) : null}
      <MembersTable
        members={filteredMembers}
        ownerUid={league?.commissioner_uid}
        canEdit={isCommissioner}
        onEdit={isCommissioner ? openEdit : undefined}
        onRemove={isCommissioner ? setRemoveTarget : undefined}
        onRestore={isCommissioner && !archived ? (member) => void openRestore(member) : undefined}
        pendingClaimMemberIds={isCommissioner ? pendingClaimMemberIds : undefined}
        memberInvites={isCommissioner ? memberInvites : undefined}
        memberClaims={isCommissioner ? memberClaims : undefined}
        onCopyInviteLink={isCommissioner ? (member) => void copyMemberLink(member) : undefined}
        onCopyInviteMessage={isCommissioner ? (member) => void copyMemberMessage(member) : undefined}
        onReinviteMember={isCommissioner ? (member) => void issueInvite(member, true) : undefined}
        onUnclaimMember={isCommissioner && !archived ? (member) => void unclaimMember(member) : undefined}
        invitingMemberId={invitingMemberId}
        emptyMessage={emptyFilterMessage(statusFilter)}
        showInactiveHelp={statusFilter === 'inactive'}
      />
      {isCommissioner && leagueId && (
        <details className="rounded-lg border border-secondary-700 p-4" data-testid="members-advanced">
          <summary className="cursor-pointer text-sm font-medium text-white">Advanced</summary>
          <div className="mt-4 space-y-6">
            <NeonSportzMemberSyncPanel
              leagueId={leagueId}
              disabled={archived}
              onApplied={() => void loadMembers()}
            />
            <ImportedMembersPromotionPanel leagueId={leagueId} onApplied={() => void loadMembers()} />
            <MemberClaimQueuePanel leagueId={leagueId} onDecision={() => void loadMembers()} />
            <JoinRequestQueuePanel
              leagueId={leagueId}
              leagueArchived={archived}
              hideWhenEmpty
              onDecision={() => void loadMembers()}
            />
            <button type="button" className="btn-secondary" onClick={openCreate} data-testid="add-member-btn">
              Add member
            </button>
          </div>
        </details>
      )}
      {isCommissioner && (
        <MemberFormModal
          open={modalOpen}
          mode={modalMode}
          leagueId={leagueId}
          initial={editingMember}
          saving={saving}
          formError={formError}
          onClose={() => {
            setModalOpen(false);
            setFormError(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
      {isCommissioner && (
        <RemoveMemberConfirmModal
          open={Boolean(removeTarget)}
          member={removeTarget}
          removing={removing}
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => void handleRemove()}
        />
      )}
      {isCommissioner && (
        <RestoreMemberModal
          open={Boolean(restoreTarget)}
          member={restoreTarget}
          teams={restoreTeams}
          teamsLoading={restoreTeamsLoading}
          restoring={restoring}
          onClose={() => setRestoreTarget(null)}
          onConfirm={(teamLabel) => void handleRestore(teamLabel)}
        />
      )}
    </div>
  );
}

export default LscMembersPage;
